import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, PristineChangeEvent, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { switchMap } from 'rxjs';

import { displayError } from '../../../validators/form-error-utils';
import { BaseInputComponent } from './base-input.component';

const noErrors: string[] = [];

/**
 * @Deprecated Use BaseInputComponent instead
 */
@Component({
  selector: 'dmb-input',
  templateUrl: './input.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    BaseInputComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatIconModule,
    MatInputModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements OnInit {
  control = input<FormControl>(new FormControl());
  type = input('text');
  placeholder = input('');
  label = input('');
  required = input(false);
  showErrors = input(true);
  autoComplete = input<MatAutocomplete>();
  helpText = input<string>();
  blurred = output<FocusEvent>();
  name = input<string>('');
  readOnly = input<boolean>();

  baseInputRef = viewChild(BaseInputComponent);
  elementRef = computed(() => this.baseInputRef()?.elementRef());

  value = signal<string>('');
  pending = signal(false);
  private controlErrors = signal<ValidationErrors | null>(null);
  private touched = signal(false);
  private pristineState = signal<unknown>(null);

  errors = computed(() => {
    const errorMap = this.controlErrors();

    if (errorMap === null || !this.showErrors() || !this.touched()) {
      return noErrors;
    }

    return Object.entries(errorMap).map(([key, value]) => displayError(key, value));
  });

  constructor() {
    toObservable(this.control)
      .pipe(
        switchMap((control) => control.events),
        takeUntilDestroyed(),
      )
      .subscribe((evt) => {
        this.value.set(evt.source.value as string);
        this.controlErrors.set(evt.source.errors);
        this.touched.set(evt.source.touched);
        this.pending.set(evt.source.pending);

        if (evt instanceof PristineChangeEvent && evt.pristine) {
          this.pristineState.set(evt.source.value);
        }
      });
  }

  ngOnInit(): void {
    const initialValue = this.control().value;
    this.value.set(initialValue);
    this.pristineState.set(initialValue);
  }

  onValueChanged(newValue: unknown) {
    const control = this.control();
    const pristineValue = this.pristineState();

    control.setValue(newValue);
    control.markAsTouched();

    if (newValue !== pristineValue) {
      control.markAsDirty();
    } else {
      control.markAsPristine();
    }
  }

  onBlur(evt: FocusEvent) {
    this.control().markAsTouched();
    this.blurred.emit(evt);
  }
}
